<script>
import FileBrowser from '@/components/file-browser/FileBrowser';
import Panel from '@/components/ui/Panel';
import { dirname, join, removeBasePath } from '@/modules/file-utilities';
import URI from 'urijs';

export default {
  name: 'LinkPanel',
  components: {
    FileBrowser,
    Panel
  },
  props: {
    page: {
      type: Object,
      required: true
    },
    site: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      anchor: '',
      anchors: [],
      editor: {},
      bookmark: null,
      isFileBrowserOpen: false,
      link: null,
      href: '',
      isRootLink: false,
      newWindow: false,
      title: ''
    };
  },
  methods: {
    show(options) {
      const editor = options.editor;
      const el = editor.getSelectedElement();
      this.bookmark = editor.getBookmark();
      this.editor = editor;
      this.isRootLink = editor.getRootElement().tagName.toLowerCase() === 'a';
      this.anchor = '';
      this.anchors = this.getAnchors();
      this.link = el.closest('a');
      this.href = this.link ? decodeURI(editor.getAttribute(this.link, 'href')) : '';
      this.newWindow = this.link ? editor.getAttribute(this.link, 'target') === '_blank' : false;
      this.title = this.link ? editor.getAttribute(this.link, 'title') : '';
      this.$refs.panel.show();

      // Create a selection around the entire link, that way if you double click on a link it
      // visually selects the entire link, not just one word
      if (this.link) {
        editor.setSelectedElement(this.link);
      }
    },

    hide() {
      this.$refs.panel.hide();
      this.href = '';
      this.newWindow = false;
      this.title = '';
    },

    getAnchors() {
      const doc = this.editor.getRootElement().ownerDocument;
      return [...doc.querySelectorAll('a[id]')].map(a => a.id || a.name);
    },

    removeLink() {
      this.$refs.panel.hide();
      this.editor.restoreBookmark(this.bookmark);
      this.editor.removeLink();
    },

    handleAnchorSelect() {
      this.href = `#${this.anchor}`;
    },

    handleUrlChange() {
      const href = this.href.replace(/^#/, '');
      this.anchor = this.anchors.includes(href) ? href : '';
    },

    handleFileBrowserSelect(type, files) {
      if (files.length) {
        const file = files[0];
        const pageUrl = new URI(this.site.url).path(URI.joinPaths(this.site.url, this.page.path));
        const fileUrl = new URI(this.site.url).path(
          URI.joinPaths(this.site.url, removeBasePath(file.path, this.site.homeFolder))
        );
        let href = fileUrl.relativeTo(pageUrl).toString();

        // If href is empty, the URL has resolved to the current page. Instead of returning an empty
        // string, we'll pass back the filename.
        if (href === '') {
          href = pageUrl.filename();
        }

        this.href = href;
        this.$refs.fileBrowserPopover.doClose();
      }
    },

    handleHide() {
      this.$refs.fileBrowserPopover.doClose();
      this.editor.focus();
    },

    handleShow() {
      this.$refs.href.focus();
    },

    handleSubmit() {
      const href = this.href;
      const title = this.title;
      const target = this.newWindow ? '_blank' : null;

      this.$refs.panel.hide();
      this.editor.restoreBookmark(this.bookmark);
      this.editor.insertLink(href, { target, title });
    },

    showFileBrowser() {
      let path = this.site.uploadFolder;

      // If the current src looks like a relative path, use that directory
      if (!this.href.match(/^https?:\/\//i)) {
        const fileUrl = new URI(this.href);
        path = join(this.site.homeFolder, dirname(fileUrl.path()));
      }

      this.isFileBrowserOpen = true;
      this.$refs.fileBrowser.open({ path });
    },

    hideFileBrowser() {
      this.$refs.fileBrowser.close();
      this.isFileBrowserOpen = false;
    }
  }
};
</script>

<template>
  <panel
    ref="panel"
    :title="$t('Link')"
    :close-on-click="!isFileBrowserOpen"
    @show="handleShow"
    @hide="handleHide"
  >
    <el-form
      label-position="top"
      novalidate
      @submit.native.prevent="handleSubmit"
    >
      <fieldset>
        <!-- URL -->
        <el-form-item :label="$t('URL')">
          <el-input
            ref="href"
            v-model="href"
            type="url"
            clearable
            prefix-icon="el-icon-fa-link"
            auto-complete="off"
            spellcheck="false"
            @input="handleUrlChange"
          >
            <template slot="append">
              <el-popover
                ref="fileBrowserPopover"
                placement="bottom"
                trigger="click"
                @show="showFileBrowser()"
                @hide="hideFileBrowser()"
              >
                <el-button slot="reference">
                  {{ $t('Browse') }}
                </el-button>

                <file-browser
                  ref="fileBrowser"
                  :base-path="site.uploadFolder"
                  :multiple="false"
                  :show-pages="true"
                  :site="site"
                  type="popover"
                  @select="handleFileBrowserSelect"
                />
              </el-popover>
            </template>
          </el-input>
        </el-form-item>

        <!-- Anchors -->
        <el-form-item v-if="anchors.length" :label="$t('Anchor')">
          <el-select
            v-model="anchor"
            placeholder=""
            @input="handleAnchorSelect"
          >
            <el-option
              v-for="(anchor, index) in anchors"
              :key="`anchor-${index}`"
              :label="anchor"
              :value="anchor"
            />
          </el-select>
        </el-form-item>

        <!-- Title -->
        <el-form-item :label="$t('Title')">
          <el-input
            v-model="title"
            type="text"
            auto-complete="off"
          />
          <div class="text-form-description">
            {{ $t('Shown when users hover over the link.') }}
          </div>
        </el-form-item>

        <el-form-item>
          <el-switch v-model="newWindow" :active-text="$t('Open in a New Window')" />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-button type="primary" native-type="submit">
              <span v-if="link">{{ $t('Update Link') }}</span>
              <span v-else>{{ $t('Insert Link') }}</span>
            </el-button>
          </el-col>
          <el-col :span="12" class="text-right">
            <el-button v-if="link && !isRootLink" :title="$t('Remove Link')" @click="removeLink">
              <i class="el-icon-fa-unlink" />
            </el-button>
          </el-col>
        </el-row>
      </fieldset>
    </el-form>
  </panel>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

//
// No styles yet
//
</style>
